import {
  Captions,
  ChevronDown,
  Loader2,
  Mic,
  MicOff,
  MonitorOff,
  MonitorUp,
  PhoneOff,
  Volume2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { LiveBadge, type RosterEntry } from '../components/teamChat/VoiceChannelRow';
import { VOICE_CALLS_PATH, VoiceCallHistory } from '../components/teamChat/VoiceCallHistory';
import { SoundboardButton } from '../components/teamChat/SoundboardButton';
import { VoiceTranscript } from '../components/teamChat/VoiceTranscript';
import { LocalShareNotice, ScreenShareStage } from '../components/teamChat/ScreenShareStage';
import { Avatar } from '../components/tasks/linear/Avatar';
import { useTestMode } from '../components/testMode/TestModeContext';
import { useI18n } from '../contexts/I18nContext';
import { canShareScreen, useVoiceRoom } from '../contexts/VoiceRoomContext';
import { useVoiceChannelPresence, useVoiceChannels } from '../hooks/useVoice';
import { useLiveTranscript, useVoiceCalls } from '../hooks/useVoiceCalls';
import { useAuthStore } from '../store/useAuthStore';

const HEADER_BUTTON =
  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors disabled:opacity-50';

/**
 * /team-chat/voice/:channelId — the inside of a voice channel, Discord-style:
 * who is in it, who is talking, and whichever screens are being shared. The
 * call itself lives in VoiceRoomProvider and follows you around the app; this
 * page is only where the screens are drawn, because a screen share needs room
 * that neither the sidebar nor a chat header can give it.
 *
 * You have to be in the channel to see a screen: LiveKit only delivers tracks
 * to participants of the room. Before joining, the page shows the roster from
 * the presence poll and a join button; after, the live room takes over.
 */
const TRANSCRIPT_OPEN_KEY = 'firmio.voice.transcriptOpen';
const RECENT_CALLS_OPEN_KEY = 'firmio.voice.recentCallsOpen';

/**
 * Whether a foldable section of this page is unfolded, remembered per browser
 * under `key`. Both sections start closed: during a screen share the stage is
 * the point, and a transcript that grows while you watch — or a list of eight
 * past calls — pushes it up the page. Storage can throw (private window,
 * blocked site data), so every access is guarded and simply falls back to
 * closed.
 */
function useFoldedSection(key: string): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  return [
    open,
    (next: boolean) => {
      setOpen(next);
      try {
        localStorage.setItem(key, next ? '1' : '0');
      } catch {
        /* not fatal — the section just forgets between reloads */
      }
    },
  ];
}

export function VoiceChannelPage() {
  const { t } = useI18n();
  const { channelId = '' } = useParams<{ channelId: string }>();
  const [searchParams] = useSearchParams();
  const me = useAuthStore((s) => s.user);
  const { enabled: testMode } = useTestMode();
  const { data: channels, isLoading } = useVoiceChannels();
  const { participants } = useVoiceChannelPresence(channelId);
  const room = useVoiceRoom();
  // A click on a thumbnail wins over the `?watch=` the sidebar arrived with,
  // and the sidebar's choice wins over "the first one".
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useFoldedSection(TRANSCRIPT_OPEN_KEY);
  const [callsOpen, setCallsOpen] = useFoldedSection(RECENT_CALLS_OPEN_KEY);

  const channel = channels?.find((c) => c.id === channelId) ?? null;
  const isHere = room.channelId === channelId;
  const connecting = isHere && room.status === 'connecting';

  // The transcript of the call we are in, growing as people talk, and the
  // channel's past calls. Both hooks run unconditionally (rules of hooks) and
  // are inert when not applicable.
  const liveCallId = isHere ? room.callId : null;
  const live = useLiveTranscript({ callId: liveCallId, active: liveCallId !== null });
  const { data: history } = useVoiceCalls({ channelId, limit: 10, live: isHere });

  const roster: RosterEntry[] = isHere
    ? room.members
    : participants.map((p) => ({ ...p, muted: null, isLocal: p.userId === me?.id }));
  const speaking = new Set(isHere ? room.speakingIds : []);
  const remoteShares = isHere ? room.screenShares.filter((s) => !s.isLocal) : [];
  const anyoneSharing = roster.some((p) => p.sharingScreen && !p.isLocal);
  const focusedId = pickedId ?? searchParams.get('watch');

  const join = () => {
    if (!channel || testMode) return;
    void room.join(channel.id, channel.name);
  };

  if (!channel) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent">
          {isLoading ? (
            <Loader2 size={26} className="animate-spin text-muted-foreground" />
          ) : (
            <Volume2 size={26} className="text-muted-foreground" />
          )}
        </span>
        {!isLoading && (
          <p className="max-w-sm text-sm text-muted-foreground">
            {t('teamChat.voiceChannelNotFound')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Volume2
          size={18}
          className={isHere ? 'shrink-0 text-emerald-500' : 'shrink-0 text-muted-foreground'}
        />
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-heading text-base font-semibold text-foreground">
            {channel.name}
          </h1>
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            {t('teamChat.voiceParticipants', { count: roster.length })}
            {isHere && room.transcribing && (
              <span
                title={t('teamChat.voiceTranscriptHint')}
                className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700"
              >
                <Captions size={12} />
                {t('teamChat.voiceTranscriptOn')}
              </span>
            )}
          </p>
        </div>
        {isHere ? (
          <div className="flex shrink-0 items-center gap-1">
            {canShareScreen() && (
              <button
                type="button"
                onClick={() => void room.toggleScreenShare()}
                aria-pressed={room.screenShareEnabled}
                title={
                  room.screenShareEnabled
                    ? t('teamChat.voiceStopSharing')
                    : t('teamChat.voiceShare')
                }
                className={`${HEADER_BUTTON} ${
                  room.screenShareEnabled
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                {room.screenShareEnabled ? <MonitorOff size={16} /> : <MonitorUp size={16} />}
                <span className="hidden sm:inline">
                  {room.screenShareEnabled
                    ? t('teamChat.voiceStopSharing')
                    : t('teamChat.voiceShare')}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => void room.toggleMic()}
              aria-pressed={!room.micEnabled}
              title={room.micEnabled ? t('teamChat.voiceMute') : t('teamChat.voiceUnmute')}
              className={`${HEADER_BUTTON} ${
                room.micEnabled
                  ? 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  : 'bg-foreground text-background'
              }`}
            >
              {room.micEnabled ? <Mic size={16} /> : <MicOff size={16} />}
            </button>
            <SoundboardButton className={HEADER_BUTTON} />
            <button
              type="button"
              onClick={() => void room.leave()}
              title={t('teamChat.voiceLeave')}
              className={`${HEADER_BUTTON} bg-red-500/15 text-red-600 hover:bg-red-500/25`}
            >
              <PhoneOff size={16} />
              <span className="hidden sm:inline">{t('teamChat.voiceLeave')}</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={join}
            disabled={testMode}
            title={testMode ? t('teamChat.voiceTestMode') : undefined}
            className={`${HEADER_BUTTON} bg-emerald-600 text-white hover:bg-emerald-700`}
          >
            <Volume2 size={16} />
            {t('teamChat.voiceJoin')}
          </button>
        )}
      </header>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
        {connecting && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            {t('teamChat.voiceConnecting')}
          </p>
        )}

        {/* The stage. Only ever populated while in the channel — see above. */}
        {isHere && room.screenShareEnabled && <LocalShareNotice />}
        {remoteShares.length > 0 && (
          <section>
            <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              {t('teamChat.voiceSharedScreens')}
            </h2>
            <ScreenShareStage shares={remoteShares} focusedId={focusedId} onFocus={setPickedId} />
          </section>
        )}
        {!isHere && anyoneSharing && (
          <p className="flex items-center gap-2 rounded-lg border border-border bg-accent/50 px-3 py-2 text-sm text-muted-foreground">
            <LiveBadge />
            {t('teamChat.voiceJoinToWatch')}
          </p>
        )}

        <section>
          <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t('teamChat.voiceMembers')}
          </h2>
          {roster.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('teamChat.voiceEmptyChannel')}</p>
          ) : (
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-3">
              {roster.map((p) => {
                const watchable = p.sharingScreen && !p.isLocal;
                return (
                  <li
                    key={p.userId}
                    className={[
                      'flex flex-col items-center gap-2 rounded-lg border bg-card px-3 py-4 text-center transition-colors',
                      speaking.has(p.userId) ? 'border-emerald-500' : 'border-border',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'rounded-full ring-4 transition-colors',
                        speaking.has(p.userId) ? 'ring-emerald-500/60' : 'ring-transparent',
                      ].join(' ')}
                    >
                      <Avatar name={p.name} size={48} />
                    </span>
                    <span className="flex max-w-full items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-foreground">
                        {p.isLocal ? t('teamChat.voiceYou') : p.name}
                      </span>
                      {p.muted && (
                        <span
                          title={t('teamChat.voiceMuted')}
                          className="shrink-0 text-muted-foreground"
                        >
                          <MicOff size={13} />
                        </span>
                      )}
                    </span>
                    {watchable && (
                      <button
                        type="button"
                        onClick={() => (isHere ? setPickedId(p.userId) : join())}
                        disabled={!isHere && testMode}
                        title={t('teamChat.voiceWatchOf', { name: p.name })}
                        className="inline-flex items-center gap-1.5 rounded-md bg-red-500/15 px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-500/25 disabled:opacity-50"
                      >
                        <LiveBadge />
                        {t('teamChat.voiceWatch')}
                      </button>
                    )}
                    {p.sharingScreen && p.isLocal && <LiveBadge />}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* What is being said, as it is said. Only for the call we are in —
            a transcript of a room you have not entered would be listening at
            the door. Past calls are below, and those are open to everyone. */}
        {isHere && room.callId && (
          <section>
            {/* Folded by default: during a screen share the stage is what people
                came for, and a transcript that grows while you watch pushes it
                up the page. The choice is remembered per browser. */}
            <h2>
              <button
                type="button"
                onClick={() => setTranscriptOpen(!transcriptOpen)}
                aria-expanded={transcriptOpen}
                className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground hover:text-foreground"
              >
                <Captions size={14} />
                {t('teamChat.voiceTranscriptLive')}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${transcriptOpen ? '' : '-rotate-90'}`}
                />
              </button>
            </h2>
            {transcriptOpen && (
              <>
                {!room.transcriptionEnabled && (
                  <p className="mb-2 text-sm text-muted-foreground">
                    {t('teamChat.voiceTranscriptOff')}
                  </p>
                )}
                {room.transcriptStopped && (
                  <p className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
                    {t(room.transcriptStopped)}
                  </p>
                )}
                <div className="max-h-96 overflow-y-auto rounded-lg border border-border bg-card px-4 py-3">
                  <VoiceTranscript
                    segments={live.segments}
                    meId={me?.id}
                    followLatest
                    emptyText={t('teamChat.voiceTranscriptEmpty')}
                  />
                </div>
              </>
            )}
          </section>
        )}

        <section>
          {/* Folded like the transcript above, and for the same reason: the
              list is history, and history should not push the live stage up
              the page. "All calls" stays reachable while folded. */}
          <div className="mb-2 flex items-center justify-between">
            <h2>
              <button
                type="button"
                onClick={() => setCallsOpen(!callsOpen)}
                aria-expanded={callsOpen}
                className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground hover:text-foreground"
              >
                {t('teamChat.voiceRecentCalls')}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${callsOpen ? '' : '-rotate-90'}`}
                />
              </button>
            </h2>
            <Link
              to={VOICE_CALLS_PATH}
              className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
            >
              {t('teamChat.voiceCallAllCalls')}
            </Link>
          </div>
          {callsOpen && (
            <VoiceCallHistory
              calls={history?.calls ?? []}
              showChannel={false}
              emptyText={t('teamChat.voiceNoCalls')}
            />
          )}
        </section>
      </div>
    </div>
  );
}
